import client from "@/src/apollo/client";
import GenericHead from "@/src/components/Head/GenericHead";
import LoadMoreContainer from "@/src/components/LoadMoreContainer";
import Lozenge from "@/src/components/accoutrements/lozenge";
import Slash from "@/src/components/accoutrements/slash";
import FlagContainer from "@/src/components/blocks/FlagContainer";
import { GetCategoryBySlug } from "@/src/gql/queries/category";
import { GET_CATEGORY_POSTS_PAGINATED } from "@/src/gql/queries/posts";
import { CategoryType, NavData } from "@/src/types";
import { Typography } from "@material-tailwind/react";
import { GetStaticProps } from "next";
import Link from "next/link";
import { getYoutubeVideosFromSingleChannel, YoutubeVideo } from "@/src/utils/youtube";
import { getSingleSpotifyShow } from "@/src/utils/spotify";
import FrontPageVideos from "@/src/components/FrontPage/FrontPageVideos";
import { SpotifyContainer, SpofiyShow } from "@/src/components/Podcast/Spotify";

export interface PreviewPostProps {
  posts: any[];
  categoryName: string;
  categoryURI: string;
  extraCategories: { [name: string]: CategoryType };
  initialCursor: string;
  initialIds: string[];
  videos: YoutubeVideo[];
  spotifyShows: SpofiyShow[];
}

const CATEGORY_TO_RICH_MEDIA = {
  'nba': {'youtube': "UULFCk2T1NMUPVK8oexgUd8tsQ", 'spotify': "5USQb597B2EDbJNInd2JSh"},
  'cricket': {'youtube': "UULFzP2XMef8PhZX0BI6_z6L4w", 'spotify': "3r9HNosFE182MGCVzoPgi3"},
  'bbl': {'youtube': "UULFzP2XMef8PhZX0BI6_z6L4w", 'spotify': "3r9HNosFE182MGCVzoPgi3"},
  'nrl': {'youtube': "UULFMReymNfmodE1OEc9fQvwQQ", 'spotify': "7LT4jzjyZQGui4P8xduwNU"},
  'epl': {'youtube': "UULFcaTPguGCmg8awoRPTRO1Mg", 'spotify': "5ZBczHmD9sReJ0Hr435PWB"},
  'nfl': {'youtube': "UULFmMgv95hbFGQujDy8nFBYJQ", 'spotify': "6l66uODx8RZXfyctKoZtw9"},
}

const CategoryHome = ({
  posts,
  categoryName,
  categoryURI,
  extraCategories = {},
  initialCursor,
  initialIds,
  videos = [],
  spotifyShows = [],
}: PreviewPostProps) => {
  const flagArticles = posts?.slice(0, 3);
  const flagArticleContainer = flagArticles?.map(function (article, index) {
    return {
      position: index === 0 ? 0 : index === 1 ? 1 : 2,
      article: { heroArticlePostItem: { nodes: [article] } },
    };
  });
  return (
    <>
      <GenericHead
        title={`SCPB ${categoryName} Supercoach News`}
        description={`SCPB ${categoryName} SuperCoach news`}
      />
      <div>
        <div className="mx-auto flex flex-col gap-4">
          {
            // @ts-ignore
            <FlagContainer flags={flagArticleContainer} />
          }
        </div>
        {videos?.length > 0 && (
          <FrontPageVideos header={`${categoryName} Videos`} videos={videos} />
        )}
        {spotifyShows?.length > 0 && (
          <div className="w-full bg-white">
            <div className="container mx-auto py-4">
              <SpotifyContainer shows={spotifyShows} />
            </div>
          </div>
        )}
        <div className="w-[100%] bg-white">
          <div className="container mx-auto flex flex-col gap-4 py-4">
            <div className="flex items-center text-2xl gap-2 px-2 md:px-0">
              <Typography variant="h2" className="whitespace-nowrap	">
                {categoryName}
              </Typography>
              <Slash />
              <div
                className={"flex items-center w-full overflow-scroll"}
                style={{ columnGap: "0.5rem" }}
              >
                {Object.values(extraCategories)
                  .slice(0, 5)
                  .map(function (category) {
                    return (
                      <Link
                        href={`${category.uri}`}
                        key={category.uri}
                        className="flex"
                      >
                        <Lozenge color="light" text={category.name} />
                      </Link>
                    );
                  })}
              </div>
            </div>
            <LoadMoreContainer
              initialArticles={posts?.slice(3)}
              ignoreIds={initialIds}
              loadMoreQuery={GET_CATEGORY_POSTS_PAGINATED}
              loadMoreVariables={{ id: categoryURI }}
              convertPayload={(object: any) => {
                const thelastCursor = object?.category?.posts?.edges
                  ? object?.category?.posts?.edges[
                      object?.category?.posts?.edges.length - 1
                    ]?.cursor
                  : "";
                return {
                  posts: object?.category?.posts?.edges.map(function (
                    article: any
                  ) {
                    return article.node;
                  }),
                  thelastCursor: object?.category?.posts?.edges
                    ? object?.category?.posts?.edges[
                        object?.category?.posts?.edges.length - 1
                      ]?.cursor
                    : "",
                  done: false,
                };
              }}
              initialCursor={initialCursor}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const theSlug = context.params?.slug?.length
    ? context.params?.slug[context.params?.slug?.length - 1]
    : "";
  const category = await client.query({
    query: GetCategoryBySlug,
    variables: { id: `/category/${theSlug}` },
  });


  
  if (!category) {
    return {
      props: { category },
      notFound: true,
    };
  }
  const initialCursor =
    category?.data?.category?.posts?.edges?.[0]?.cursor || "";
  const posts =
    category.data?.category?.posts?.edges.map(function (edge: object) {
      //@ts-ignore
      return edge.node;
    }) || [];

  const extraCategories: { [name: string]: CategoryType } = {};
  const categoryName = category?.data?.category?.name || "";
  const ids: string[] = [];
  posts.forEach(function (post: any) {
    if (post.categories?.edges) {
      post.categories?.edges.forEach(function (category: any) {
        if (category.node.name !== categoryName) {
          extraCategories[category.node.name] = {
            name: category.node.name,
            uri: category.node.uri,
          };
        }
      });
    }
    ids.push(post.id);
  });

  const categoryURI = `/category/${theSlug}`;
  
  // Fetch rich media if category is in CATEGORY_TO_RICH_MEDIA
  let videos: YoutubeVideo[] = [];
  let spotifyShows: SpofiyShow[] = [];
  
  if (theSlug && CATEGORY_TO_RICH_MEDIA[theSlug as keyof typeof CATEGORY_TO_RICH_MEDIA]) {
    const richMedia = CATEGORY_TO_RICH_MEDIA[theSlug as keyof typeof CATEGORY_TO_RICH_MEDIA];
    
    // Fetch YouTube videos
    if (richMedia.youtube) {
      try {
        videos = await getYoutubeVideosFromSingleChannel(richMedia.youtube);
        // Limit to 5 videos
        videos = videos.slice(0, 7);
      } catch (error) {
        console.error('Error fetching YouTube videos:', error);
        videos = [];
      }
    }
    // Fetch Spotify episodes
    if (richMedia.spotify) {
      try {
        spotifyShows = await getSingleSpotifyShow(richMedia.spotify, 5);
      } catch (error) {
        console.error('Error fetching Spotify episodes:', error);
        spotifyShows = [];
      }
    }
  }
  
  return {
    props: {
      posts,
      categoryName,
      categoryURI,
      extraCategories,
      initialCursor,
      initialIds: ids,
      videos,
      spotifyShows,
    },
    revalidate: 60 * 30,
  };
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export default CategoryHome;
